// Custom hook for managing Service Worker interactions
import { useState, useEffect, useCallback } from 'react';
import { serviceWorkerManager } from '../services/service-worker-manager';

export interface ServiceWorkerStatus {
  isInitialized: boolean;
  isUpdateAvailable: boolean;
  currentVersion: string;
  newVersion: string;
  isLoading: boolean;
  error: string | null;
}

export const useServiceWorker = () => {
  const [status, setStatus] = useState<ServiceWorkerStatus>({
    isInitialized: false,
    isUpdateAvailable: false,
    currentVersion: '',
    newVersion: '',
    isLoading: false,
    error: null
  });

  // Initialize service worker on mount (non-blocking)
  useEffect(() => {
    const initServiceWorker = async () => {
      try {
        // Check if service worker manager is available
        if (!serviceWorkerManager || typeof serviceWorkerManager.initialize !== 'function') {
          console.warn('Service Worker manager not available');
          setStatus(prev => ({ ...prev, isInitialized: true, isLoading: false }));
          return;
        }

        setStatus(prev => ({ ...prev, isLoading: true, error: null }));
        
        // Initialize with timeout to prevent blocking
        const success = await Promise.race([
          serviceWorkerManager.initialize(),
          new Promise<boolean>((resolve) => {
            setTimeout(() => {
              console.warn('Service Worker initialization timed out, continuing...');
              resolve(false);
            }, 3000);
          })
        ]);
        
        if (success) {
          // Check for updates after initialization (non-blocking)
          try {
            const versionInfo = await serviceWorkerManager.checkVersion();
            if (versionInfo && versionInfo.currentVersion) {
              // Check if we're in the middle of an update
              const isUpdating = sessionStorage.getItem('sw-update-in-progress') === 'true';
              const updateAvailable = isUpdating ? false : serviceWorkerManager.isUpdateAvailable();
              
              setStatus(prev => ({
                ...prev,
                isInitialized: true,
                isUpdateAvailable: updateAvailable,
                currentVersion: versionInfo.currentVersion,
                isLoading: false
              }));
            } else {
              // Fallback: get version from DOM if versionInfo is invalid
              const fallbackVersion = serviceWorkerManager.getVersionFromDOM();
              setStatus(prev => ({
                ...prev,
                isInitialized: true,
                currentVersion: fallbackVersion,
                isLoading: false
              }));
            }
          } catch (error) {
            console.warn('Version check failed:', error);
            // Fallback: get version from DOM
            const fallbackVersion = serviceWorkerManager.getVersionFromDOM();
            setStatus(prev => ({
              ...prev,
              isInitialized: true,
              currentVersion: fallbackVersion,
              isLoading: false
            }));
          }
        } else {
          setStatus(prev => ({
            ...prev,
            isLoading: false,
            error: 'Service Worker initialization failed or timed out'
          }));
        }
      } catch (error) {
        console.error('Service Worker initialization failed:', error);
        setStatus(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }));
      }
    };

    // Initialize in background, don't block the app
    setTimeout(initServiceWorker, 100);

    // Listen for service worker state changes to clear update flags
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'VERSION_CHANGED') {
        // Clear update flags when version changes
        sessionStorage.removeItem('sw-update-in-progress');
        sessionStorage.removeItem('sw-update-version');
        setStatus(prev => ({ ...prev, isUpdateAvailable: false }));
      }
    };

    // Add event listener for service worker messages
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    // Periodic cleanup of stale update flags (every 5 minutes)
    const cleanupInterval = setInterval(() => {
      const isUpdating = sessionStorage.getItem('sw-update-in-progress');
      const updateVersion = sessionStorage.getItem('sw-update-version');
      
      if (isUpdating === 'true' && updateVersion) {
        // If we've been "updating" for more than 10 minutes, clear the flag
        const updateTime = new Date(updateVersion).getTime();
        const now = new Date().getTime();
        const timeDiff = now - updateTime;
        
        if (timeDiff > 10 * 60 * 1000) { // 10 minutes
          console.log('Clearing stale update flags');
          sessionStorage.removeItem('sw-update-in-progress');
          sessionStorage.removeItem('sw-update-version');
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    // Cleanup on unmount
    return () => {
      clearInterval(cleanupInterval);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
      if (serviceWorkerManager && typeof serviceWorkerManager.destroy === 'function') {
        serviceWorkerManager.destroy();
      }
    };
  }, []);

  // Check for updates
  const checkForUpdates = useCallback(async () => {
    try {
      if (!serviceWorkerManager || typeof serviceWorkerManager.checkVersion !== 'function') {
        throw new Error('Service Worker manager not available');
      }

      setStatus(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Check if we're in the middle of an update
      const isUpdating = sessionStorage.getItem('sw-update-in-progress') === 'true';
      if (isUpdating) {
        setStatus(prev => ({
          ...prev,
          isUpdateAvailable: false,
          isLoading: false
        }));
        return;
      }
      
      const versionInfo = await serviceWorkerManager.checkVersion();
      
      if (versionInfo && versionInfo.currentVersion) {
        const updateAvailable = serviceWorkerManager.isUpdateAvailable();
        setStatus(prev => ({
          ...prev,
          isUpdateAvailable: updateAvailable,
          currentVersion: versionInfo.currentVersion,
          isLoading: false
        }));
      } else {
        // Fallback: get version from DOM if versionInfo is invalid
        const fallbackVersion = serviceWorkerManager.getVersionFromDOM();
        setStatus(prev => ({
          ...prev,
          isLoading: false,
          currentVersion: fallbackVersion
        }));
      }
    } catch (error) {
      console.error('Update check failed:', error);
      // Fallback: get version from DOM
      if (serviceWorkerManager && typeof serviceWorkerManager.getVersionFromDOM === 'function') {
        const fallbackVersion = serviceWorkerManager.getVersionFromDOM();
        setStatus(prev => ({
          ...prev,
          isLoading: false,
          currentVersion: fallbackVersion
        }));
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setStatus(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Apply update
  const applyUpdate = useCallback(async () => {
    try {
      if (!serviceWorkerManager || typeof serviceWorkerManager.skipWaiting !== 'function') {
        throw new Error('Service Worker manager not available');
      }

      setStatus(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Mark that we're updating to prevent showing the popup again
      sessionStorage.setItem('sw-update-in-progress', 'true');
      sessionStorage.setItem('sw-update-version', status.currentVersion);
      
      // Update local state immediately to hide the notification
      setStatus(prev => ({ 
        ...prev, 
        isUpdateAvailable: false, 
        isLoading: false 
      }));
      
      await serviceWorkerManager.skipWaiting();
      
      // Small delay to ensure state is updated before reload
      setTimeout(() => {
        if (serviceWorkerManager && typeof serviceWorkerManager.reloadPage === 'function') {
          serviceWorkerManager.reloadPage();
        } else {
          window.location.reload();
        }
      }, 100);
    } catch (error) {
      console.error('Update application failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setStatus(prev => ({ ...prev, isLoading: false, error: errorMessage }));
      throw error;
    }
  }, [status.currentVersion]);

  // Clear cache
  const clearCache = useCallback(async () => {
    try {
      if (!serviceWorkerManager || typeof serviceWorkerManager.clearDataCache !== 'function') {
        throw new Error('Service Worker manager not available');
      }

      setStatus(prev => ({ ...prev, isLoading: true, error: null }));
      
      await serviceWorkerManager.clearDataCache();
      console.log('Cache cleared successfully');
      
      setStatus(prev => ({ ...prev, isLoading: false }));
    } catch (error) {
      console.error('Cache clearing failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setStatus(prev => ({ ...prev, isLoading: false, error: errorMessage }));
      throw error;
    }
  }, []);

  // Get cache information
  const getCacheInfo = useCallback(async () => {
    try {
      if (!serviceWorkerManager || typeof serviceWorkerManager.getCacheInfo !== 'function') {
        throw new Error('Service Worker manager not available');
      }

      const cacheInfo = await serviceWorkerManager.getCacheInfo();
      return cacheInfo;
    } catch (error) {
      console.error('Failed to get cache info:', error);
      throw error;
    }
  }, []);

  // Reset error state
  const resetError = useCallback(() => {
    setStatus(prev => ({ ...prev, error: null }));
  }, []);

  // Get service worker status
  const getWorkerStatus = useCallback(() => {
    if (serviceWorkerManager && typeof serviceWorkerManager.getStatus === 'function') {
      return serviceWorkerManager.getStatus();
    }
    return null;
  }, []);

  return {
    // State
    ...status,
    
    // Actions
    checkForUpdates,
    applyUpdate,
    clearCache,
    getCacheInfo,
    getWorkerStatus,
    resetError
  };
};
